import { useState } from "react";
import { Badge, Center, Group, Loader, Text } from "@mantine/core";
import { IconArrowsMaximize, IconPhoto, IconTag } from "@tabler/icons-react";
import { ProductProposal } from "@/types/trackLabeling";
import { MISSING_IMAGE_PLACEHOLDER } from "../../config/constants";
import { productImageCache, useCachedImageUrl } from "@/utils/imageCache";
import { toPercent } from "./utils";
import { CustomCheckCircle, HotkeyBadge } from "./shared";

type PredictionGridProps = {
  products: ProductProposal[];
  /** Live product metadata is still being fetched — show a loader instead of an empty state. */
  loading: boolean;
  selectedProduct: string;
  onSelectProduct: (productId: string) => void;
  onConfirmSelection: () => void;
  /** Opens the all-images modal for a product. */
  onOpenImages: (product: { id: string; title: string }) => void;
  /** Current search text — only used to pick the empty-state message. */
  query: string;
};

const ZoomableCandidateImage = ({
  id,
  src,
  alt,
  onExpand,
}: {
  /** Stable product_id — cache key so the image survives task switches. */
  id: string;
  src: string | undefined;
  alt: string;
  onExpand: () => void;
}) => {
  const [isZooming, setIsZooming] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const resolvedSrc = useCachedImageUrl(productImageCache, id, src);

  if (!resolvedSrc) {
    return (
      <div className="mb-5 flex min-h-0 w-full flex-1 items-center justify-center rounded-[20px] border border-[#F0F3F5] bg-[#FAFBFC]">
        <IconPhoto size={40} className="text-[#CBD5E1]" />
      </div>
    );
  }

  return (
    <div
      className="relative mb-5 flex min-h-0 w-full flex-1 cursor-zoom-in items-center justify-center overflow-hidden rounded-[20px] border border-[#F0F3F5] bg-[#FAFBFC] p-4 transition-colors group-hover:bg-[#F4F7F9]"
      onMouseEnter={() => setIsZooming(true)}
      onMouseLeave={() => {
        setIsZooming(false);
        setMousePosition({ x: 50, y: 50 });
      }}
      onMouseMove={(event) => {
        const imageContainer = event.currentTarget.getBoundingClientRect();
        const x =
          ((event.clientX - imageContainer.left) / imageContainer.width) * 100;
        const y =
          ((event.clientY - imageContainer.top) / imageContainer.height) * 100;
        setMousePosition({
          x: Math.max(0, Math.min(100, x)),
          y: Math.max(0, Math.min(100, y)),
        });
      }}
    >
      <img
        src={resolvedSrc}
        alt={alt}
        className="h-full w-full object-contain will-change-transform"
        style={{
          transformOrigin: `${mousePosition.x}% ${mousePosition.y}%`,
          transform: isZooming ? "scale(1.7)" : "scale(1)",
          transition: "transform 220ms ease-out",
        }}
        onError={(event) => {
          const imageTarget = event.currentTarget;
          imageTarget.onerror = null;
          imageTarget.src = MISSING_IMAGE_PLACEHOLDER;
        }}
      />
      <button
        type="button"
        title="View all images"
        aria-label="View all images"
        className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#475569] opacity-0 shadow-sm transition-opacity hover:bg-white group-hover:opacity-100"
        onClick={(event) => {
          event.stopPropagation();
          onExpand();
        }}
      >
        <IconArrowsMaximize size={18} />
      </button>
    </div>
  );
};

export const PredictionGrid = ({
  products,
  loading,
  selectedProduct,
  onSelectProduct,
  onConfirmSelection,
  onOpenImages,
  query,
}: PredictionGridProps) => {
  return (
    <div className="custom-scrollbar flex-1 min-h-0 -mr-3 overflow-y-auto pr-3 opacity-0 animate-[fadeIn_0.5s_ease-out_forwards]">
      <div className="grid w-full grid-cols-1 auto-rows-[338px] gap-6 p-3 sm:grid-cols-2 2xl:grid-cols-3">
        {products.map((product, index) => {
          const isSelected = selectedProduct === product.product_id;
          const imageSrc = product.imageUrl;
          // Structured display when the product has full catalogue metadata.
          const structured = product.cluster && product.variant;
          const brandName = product.cluster?.brand.name;
          const displayName = product.cluster?.name ?? product.title;
          const scoreText = product.fromProductSet
            ? null
            : toPercent(product.final_score);
          const priceText =
            product.price != null ? `$${product.price.toFixed(2)}` : null;

          return (
            <div
              key={product.product_id}
              onClick={() => onSelectProduct(product.product_id)}
              onDoubleClick={onConfirmSelection}
              className={`group relative flex h-full cursor-pointer flex-col items-center rounded-[28px] bg-white px-4 pb-4 pt-10 text-center transition-all duration-200 ${
                isSelected
                  ? "border-[2px] border-[#5A6EFA] shadow-md"
                  : "border-[2px] border-transparent shadow-[0_4px_20px_-10px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_-10px_rgba(0,0,0,0.08)]"
              }`}
            >
              <HotkeyBadge hotkey={`${index + 1}`} />
              {scoreText && (
                <Text
                  size="xs"
                  fw={600}
                  c="dimmed"
                  className="absolute left-1/2 top-[18px] -translate-x-1/2"
                >
                  {scoreText}
                </Text>
              )}
              {(isSelected || priceText) && (
                <Group
                  gap="xs"
                  align="center"
                  wrap="nowrap"
                  className="absolute right-4 top-3"
                >
                  {isSelected && <CustomCheckCircle />}
                  {priceText && (
                    <Badge
                      size="lg"
                      variant="light"
                      leftSection={<IconTag size={14} />}
                    >
                      {priceText}
                    </Badge>
                  )}
                </Group>
              )}

              <ZoomableCandidateImage
                id={product.product_id}
                src={imageSrc}
                alt={displayName}
                onExpand={() =>
                  onOpenImages({ id: product.product_id, title: displayName })
                }
              />

              <div className="flex w-full shrink-0 flex-col items-center gap-1 px-2">
                {brandName && (
                  <Text size="xs" fw={500} c="dimmed" lineClamp={1}>
                    {brandName}
                  </Text>
                )}
                <Text
                  fw={700}
                  size="sm"
                  lineClamp={2}
                  c="#1e293b"
                  title={displayName}
                >
                  {displayName}
                </Text>
                {structured && (
                  <Text fw={800} size="xl" c="#1e293b">
                    {product.variant}
                  </Text>
                )}
              </div>
            </div>
          );
        })}
        {products.length === 0 &&
          (loading ? (
            <Center className="col-span-full" h={200}>
              <Loader />
            </Center>
          ) : (
            <div className="col-span-full rounded-[20px] border border-dashed border-[#D5DDE5] bg-white p-8 text-center">
              <p className="text-[14px] text-[#64748b]">
                {query.trim()
                  ? "No products match your search."
                  : "No product proposals found for this track."}
              </p>
            </div>
          ))}
      </div>
    </div>
  );
};
